import styled from 'styled-components'

/**
 * Tokens lifted from the SureCo Design System "Dashboard Box" (Figma node
 * 16295:1228), so these tiles match the component rather than approximating it.
 */
const TILE = {
  text: '#112a43',
  muted: '#617d99',
  border: '#bcccdc',
  hoverBackground: '#f0f4f8',
  hoverBorder: '#d9e2ec',
  /** The design's "focused" state — reused here to mark the active filter. */
  activeBorder: '#0a939b',
} as const

export const PILL_TONES = {
  wex: { color: '#2d5f9e', background: '#eff5fb', border: '#b9d4ee' },
  marqeta: { color: '#6a4ca8', background: '#f4effb', border: '#c9b8ec' },
} as const

export type PillTone = keyof typeof PILL_TONES

/**
 * Flex rather than a grid of equal columns: a tile holding two provider
 * readings needs more width than one holding a single number, and equal columns
 * forced the wider ones to wrap their figures. Each tile takes the width its
 * own content needs and then shares what is left over, so `10,921` stays on one
 * line and the row still fills the page.
 */
export const TilesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`

export const Tile = styled.div<{ $active: boolean }>`
  /* Sizes to its content and stays there. Growing made the last tile on a
     wrapped row stretch across the whole page, because it was the only item
     left to absorb the slack. The floor keeps a single-number tile from
     collapsing to the width of "642". */
  flex: 0 1 auto;
  min-width: 236px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  padding: 12px;
  min-height: 92px;
  background: #fff;
  border: 1px solid
    ${({ $active }) => ($active ? TILE.activeBorder : TILE.border)};
  border-radius: 4px;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? '#fff' : TILE.hoverBackground)};
    border-color: ${({ $active }) =>
      $active ? TILE.activeBorder : TILE.hoverBorder};
  }
`

export const TileHeader = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  width: 100%;
`

export const TileTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${TILE.text};
  white-space: nowrap;
`

export const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  width: 100%;
`

/**
 * Wraps rather than overflows. The two-provider tiles carry a pill and a number
 * twice over, which needs roughly 360px on one line — more than a tile gets once
 * five sit across the page. Without wrapping, flex children default to
 * `min-width: auto`, refuse to shrink, and the number escapes the tile's border.
 */
/**
 * `max-content` is what makes the tile widen instead of wrapping: it sets the
 * tile's minimum width to whatever the readings need on one line. The caption
 * below is free to wrap, so a long description does not drag the tile wider.
 */
export const ValueRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  width: 100%;
  min-width: max-content;
`

/** Vertical rule between the two provider readings. */
export const ValueDivider = styled.span`
  align-self: stretch;
  width: 1px;
  background: ${TILE.border};
`

/**
 * One reading and its click target. The design's box carries a single focused
 * state, but each number here filters the grid to a different set, so each is
 * its own button and the tile border lights when any of them is active.
 */
export const ValueButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  gap: 2px;
  align-items: center;
  min-width: 0;
  padding: 2px 4px;
  margin: -2px -4px;
  background: ${({ $active }) =>
    $active ? 'rgba(10, 147, 155, 0.08)' : 'none'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font: inherit;
  transition: background 0.15s;

  &:hover {
    background: rgba(10, 147, 155, 0.08);
  }
  &:disabled {
    cursor: default;
    background: none;
  }
`

export const Pill = styled.span<{ $tone: PillTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
  color: ${({ $tone }) => PILL_TONES[$tone].color};
  background: ${({ $tone }) => PILL_TONES[$tone].background};
  border: 1px solid ${({ $tone }) => PILL_TONES[$tone].border};
  border-radius: 4px;
`

export const Number = styled.span`
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  white-space: nowrap;
  color: ${TILE.text};
`

export const Caption = styled.span`
  width: 100%;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${TILE.muted};
`
